import { parseMoney } from './financeLedger';

const TYPES = ['all', 'income', 'expense', 'transfer', 'adjustment'];
const SORTS = ['date-desc', 'date-asc', 'amount-desc', 'amount-asc'];
const emptyTotals = () => ({ income: 0, expense: 0, adjustment: 0, transfer: 0, count: 0 });

export const defaultLedgerQuery = () => ({
  keyword: '', type: 'all', category: 'all', period: 'month',
  dateFrom: '', dateTo: '', minAmount: '', maxAmount: '', sort: 'date-desc',
});

function isCalendarDate(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value) || +value.slice(0, 4) < 1) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

function transactionTime(value) {
  if (isCalendarDate(value)) return Date.parse(`${value}T00:00:00+08:00`);
  if (typeof value !== 'string' || !isCalendarDate(value.slice(0, 10)) || !/T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,6})?)?(?:Z|[+-]\d{2}:\d{2})$/.test(value)) return NaN;
  return Date.parse(value);
}

function amountBound(value) {
  if (value === '' || value === undefined || value === null) return null;
  const amount = parseMoney(value);
  return Number.isFinite(amount) && amount >= 0 ? Math.round(amount * 100) : NaN;
}

/** Read-only query: results never replace the ledger snapshot used for saving. */
export function queryLedgerTransactions(ledger, options = {}) {
  const query = { ...defaultLedgerQuery(), accountId: 'all', currency: 'TWD', ...options };
  let periodLabel = query.period === 'all' ? '全部月份' : query.month || '所選月份';
  const fail = error => ({ transactions: [], error, totals: emptyTotals(), periodLabel, categories: [] });
  if (!Array.isArray(ledger?.accounts) || !Array.isArray(ledger?.transactions)) return fail('帳本資料不完整，請重新讀取。');
  if (!/^[A-Z]{3}$/.test(query.currency || '')) return fail('請先選擇顯示幣別。');
  if (!['month', 'all', 'custom'].includes(query.period)) return fail('請選擇有效的查詢期間。');
  if (!TYPES.includes(query.type) || !SORTS.includes(query.sort) || typeof query.category !== 'string' || typeof query.keyword !== 'string') return fail('查詢條件有誤，請清除條件後再試。');

  let from = '', to = '';
  if (query.period === 'month') {
    if (typeof query.month !== 'string' || !/^\d{4}-(?:0[1-9]|1[0-2])$/.test(query.month) || +query.month.slice(0, 4) < 1) return fail('請選擇有效的統計月份。');
  } else if (query.period === 'custom') {
    from = query.dateFrom; to = query.dateTo;
    if (!from && !to) return fail('自訂期間請至少填寫開始或結束日期。');
    if ((from && !isCalendarDate(from)) || (to && !isCalendarDate(to))) return fail('請填寫有效的開始與結束日期。');
    if (from && to && from > to) return fail('開始日期不可晚於結束日期。');
    periodLabel = from && to ? `${from} ～ ${to}` : from ? `${from} 起` : `截至 ${to}`;
  }
  const minimum = amountBound(query.minAmount), maximum = amountBound(query.maxAmount);
  if (Number.isNaN(minimum) || Number.isNaN(maximum)) return fail('查詢金額需為零或正數，最多兩位小數。');
  if (minimum !== null && maximum !== null && minimum > maximum) return fail('最低金額不可高於最高金額。');

  const accounts = new Map(ledger.accounts.map(account => [account.id, account]));
  const selectedId = query.accountId && query.accountId !== 'all' ? query.accountId : null;
  if (selectedId && (!accounts.has(selectedId) || accounts.get(selectedId).currency !== query.currency)) return fail('選取的帳戶與目前幣別不符，請重新選擇帳戶。');
  const relevantIds = new Set(ledger.accounts.filter(account => account.currency === query.currency && (!selectedId || account.id === selectedId)).map(account => account.id));
  const candidates = [];
  const categories = new Set();
  const keyword = query.keyword.trim().toLocaleLowerCase();

  for (let index = 0; index < ledger.transactions.length; index += 1) {
    const row = ledger.transactions[index];
    const source = accounts.get(row.account_id), target = accounts.get(row.to_account_id);
    if (!relevantIds.has(row.account_id) && !(row.type === 'transfer' && relevantIds.has(row.to_account_id))) continue;
    // A malformed cross-currency transfer must never enter a combined currency total.
    if (!source || source.currency !== query.currency || (row.type === 'transfer' && (!target || target.currency !== query.currency))) return fail('帳本含無法核對幣別的轉帳，請先修正交易。');
    const time = transactionTime(row.date), amount = parseMoney(row.amount);
    if (!Number.isFinite(time) || !Number.isFinite(amount) || !TYPES.slice(1).includes(row.type) || (row.type !== 'adjustment' && amount <= 0)) return fail('帳本含無效的日期或交易金額，請先確認資料。');
    const day = new Date(time + 8 * 60 * 60 * 1000).toISOString().slice(0, 10);
    if (query.period === 'month' && day.slice(0, 7) !== query.month) continue;
    if (query.period === 'custom' && ((from && day < from) || (to && day > to))) continue;
    const category = row.category || '未分類';
    categories.add(category);
    if (query.type !== 'all' && row.type !== query.type) continue;
    if (query.category !== 'all' && category !== query.category) continue;
    const amountCents = Math.round(amount * 100), magnitude = Math.abs(amountCents);
    if ((minimum !== null && magnitude < minimum) || (maximum !== null && magnitude > maximum)) continue;
    if (keyword) {
      const content = [row.name, row.note, row.category, row.subcategory, row.project, source.name, target?.name, ...(row.tags || [])].filter(Boolean).join(' ').toLocaleLowerCase();
      if (!content.includes(keyword)) continue;
    }
    candidates.push({ row, index, time, magnitude, amountCents });
  }

  const direction = query.sort.endsWith('-asc') ? 1 : -1;
  const key = query.sort.startsWith('amount') ? 'magnitude' : 'time';
  candidates.sort((left, right) => direction * (left[key] - right[key]) || left.index - right.index);
  const totals = emptyTotals();
  for (const { row, amountCents } of candidates) totals[row.type] += amountCents;
  for (const type of TYPES.slice(1)) totals[type] /= 100;
  totals.count = candidates.length;
  return { transactions: candidates.map(item => item.row), error: '', totals, periodLabel, categories: [...categories].sort((left, right) => left.localeCompare(right, 'zh-TW')) };
}
